import{ FC, useEffect, useMemo, useState } from 'react';
import { Alert, Spin, Typography } from 'antd';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { setUserWithTokens } from '@ackee/petrus';

import LoginForm from '../LoginForm';
import AuthPage from '../AuthPage';
import CookiebotWidget from '../CookiebotWidget';
import config from 'config';
import { SALESFORCE_ACCESS_TOKEN_STORAGE_KEY } from '../../constantsSalesforce';

const { Paragraph } = Typography;

function decodeJwtExp(token: string): number | null {
    try {
        const [, payload] = token.split('.');
        if (!payload) return null;
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const json = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
                .join(''),
        );
        const parsed = JSON.parse(json) as { exp?: number };
        return typeof parsed.exp === 'number' ? parsed.exp : null;
    } catch {
        return null;
    }
}

function normalizeApprovedUsers(value: unknown): string[] {
    const raw =
        Array.isArray(value) ? value
        : (value as any)?.approvedUsers ?? (value as any)?.data ?? (value as any)?.users ?? [];

    if (!Array.isArray(raw)) return [];
    return raw.filter((x) => typeof x === 'string') as string[];
}

function normalizeSalesforceUser(value: unknown): Record<string, unknown> {
    if (Array.isArray(value)) return (value[0] ?? {}) as Record<string, unknown>;
    if (value && typeof value === 'object') return value as Record<string, unknown>;
    return {};
}

const LoginPage: FC = () => {
    const dispatch = useDispatch();
    const { search } = useLocation();
    const [verifying, setVerifying] = useState(false);
    const [verifyError, setVerifyError] = useState(false);

    const { token, isSalesforce, redirectUrl } = useMemo(() => {
        const params = new URLSearchParams(search);
        return {
            token: params.get('token'),
            isSalesforce: params.get('isSalesforce') === 'true',
            redirectUrl: params.get('redirectUrl'),
        };
    }, [search]);

    useEffect(() => {
        if (!token || !isSalesforce) return;

        let cancelled = false;
        setVerifying(true);
        setVerifyError(false);

        fetch(`${config.api.base}${config.api.salesforceMagicLoginVerify}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
        })
            .then(async (res) => {
                const data = await res.json().catch(() => null);
                if (!res.ok || !data) throw new Error('verify_failed');
                return data;
            })
            .then(async (rawUser) => {
                if (cancelled) return;
                const email = rawUser?.emailId ?? rawUser?.email;
                if (!email) throw new Error('missing_email');

                if (redirectUrl) {
                    const destination = new URL(redirectUrl, window.location.origin);
                    destination.searchParams.set('token', token);
                    if (rawUser?.userId) destination.searchParams.set('userId', String(rawUser.userId));
                    destination.searchParams.set('email', String(email));
                    localStorage.removeItem(SALESFORCE_ACCESS_TOKEN_STORAGE_KEY);
                    window.location.replace(destination.toString());
                    return;
                }

                const [salesforceUserRes, approvedUsersRes] = await Promise.all([
                    fetch(`${config.api.base}${config.api.users}/${encodeURIComponent(String(email))}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    }),
                    fetch(`${config.api.base}${config.api.users}/document/approved`, {
                        headers: { Authorization: `Bearer ${token}` },
                    }),
                ]);

                const salesforceUserRaw = await salesforceUserRes.json().catch(() => ({}));
                const approvedUsersRaw = await approvedUsersRes.json().catch(() => ([]));
                const salesforceUser = normalizeSalesforceUser(salesforceUserRaw);
                const approvedUsers = normalizeApprovedUsers(approvedUsersRaw);

                const exp = decodeJwtExp(token);
                const expiration = new Date(((exp ?? Math.floor(Date.now() / 1000) + 60 * 60) as number) * 1000).toISOString();

                const user = {
                    username: rawUser?.userId ?? String(email),
                    customUserId: rawUser?.userId,
                    email: String(email),
                    ...rawUser,
                    ...salesforceUser,
                    approvedUsers,
                };

                dispatch(setUserWithTokens(user, {
                    accessToken: { token, expiration },
                    refreshToken: { token: null },
                }));
            })
            .catch(() => {
                if (!cancelled) setVerifyError(true);
            })
            .finally(() => {
                if (!cancelled) setVerifying(false);
            });

        return () => { cancelled = true; };
    }, [dispatch, token, isSalesforce, redirectUrl]);

    return (
        <AuthPage>
            <CookiebotWidget />
            {verifying ? (
                <div style={{ width: '100%', padding: '2rem 1rem 3rem', textAlign: 'center' }}>
                    <Spin size="large" />
                    <Paragraph style={{ marginTop: '1rem' }}>
                        Signing you in…
                    </Paragraph>
                </div>
            ) : verifyError ? (
                <div style={{ width: '100%', padding: '2rem 1rem 3rem', textAlign: 'center' }}>
                    <Alert type="error" showIcon message="The link has expired or is invalid. Please request a new one." />
                </div>
            ) : (
                <LoginForm />
            )}
        </AuthPage>
    );
};

export default LoginPage;
